/**
 * Reusable multi-select + bulk-delete helpers for list pages.
 *
 * Pair with the shared <DataTable selectedIds onSelectionChange />, or wire the same Set into any
 * raw table. `useRowSelection` holds the selected-id Set; <BulkDeleteBar> renders the action bar
 * that appears once ≥1 row is selected and runs a hard delete per id (with a confirm).
 */
import { useCallback, useState } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";

export function useRowSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const clear = useCallback(() => setSelectedIds(new Set()), []);
  const toggle = useCallback(
    (id: string) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  return { selectedIds, setSelectedIds, clear, toggle, count: selectedIds.size };
}

export function BulkDeleteBar({
  count,
  onDelete,
  onClear,
  noun = "mục",
  pending = false,
}: {
  count: number;
  /** Delete the current selection (already confirmed). May throw. */
  onDelete: () => void | Promise<void>;
  onClear: () => void;
  /** What is being deleted, for the confirm text — e.g. "phòng", "dịch vụ". */
  noun?: string;
  pending?: boolean;
}) {
  if (count === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-muted px-3 py-2 sm:gap-3 sm:px-4">
      <strong className="text-sm font-semibold text-foreground">Đã chọn {count}</strong>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        disabled={pending}
        onClick={() => {
          if (confirm(`Xoá vĩnh viễn ${count} ${noun} đã chọn? Không thể hoàn tác.`)) {
            void onDelete();
          }
        }}
      >
        <Trash2 />
        {pending ? "Đang xoá…" : `Xoá đã chọn (${count})`}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onClear}>
        Bỏ chọn
      </Button>
    </div>
  );
}

/** Delete a set of ids concurrently; resolves with the ids that failed (e.g. FK-blocked). */
export async function runBulkDelete(
  ids: string[],
  remove: (id: string) => Promise<void>,
): Promise<{ failed: string[] }> {
  const results = await Promise.allSettled(ids.map((id) => remove(id)));
  const failed = ids.filter((_, i) => results[i].status === "rejected");
  return { failed };
}
